import { z } from 'zod/v4';

import { DepanelizationRuntype } from './pcbPanelPreferenceBackendTypes';

// POST
export type PanelDetailsPostDTO = z.infer<typeof PanelDetailsPostRuntype>;
export const PanelDetailsPostRuntype = z.object({
    row_count: z.number(),
    column_count: z.number(),
    horizontal_spacing_in_mm: z.string(),
    vertical_spacing_in_mm: z.string(),
    min_milling_distance_in_mm: z.string(),
    max_x_outs: z.number().nullable().optional(),
    panel_preference: z.string().nullable().optional(),
    padding: z.object({
        top_in_mm: z.string(),
        bottom_in_mm: z.string(),
        left_in_mm: z.string(),
        right_in_mm: z.string(),
    }),
    depanelization: DepanelizationRuntype,
    pcb_is_rotated: z.boolean(),
});

export type ExistingPanelContentDTO = z.infer<typeof ExistingPanelContentRuntype>;
export const ExistingPanelContentRuntype = z.object({
    pcb: z.string(),
    assembly_id: z.string().optional(),
    number_of_pcbs: z.number(),
    panel_width: z.string(),
    panel_height: z.string(),
    depanelization: DepanelizationRuntype,
    pcb_is_rotated: z.boolean(),
    max_x_outs: z.number().nullable().optional(),
    id: z.string().optional(),
});

export type ExistingPanelPostDTO = z.infer<typeof ExistingPanelPostRuntype>;
export const ExistingPanelPostRuntype = z.object({
    type: z.literal('Existing'),
    data: ExistingPanelContentRuntype,
});

export type SimplePanelContentDTO = z.infer<typeof SimplePanelContentRuntype>;
export const SimplePanelContentRuntype = z.object({
    number_of_pcbs: z.number(),
    panel_width: z.string(),
    panel_height: z.string(),
    depanelization: DepanelizationRuntype,
    max_x_outs: z.number().nullable().optional(),
});

export type SimplePanelPostDTO = z.infer<typeof SimplePanelPostRuntype>;
export const SimplePanelPostRuntype = z.object({
    type: z.literal('Simple'),
    data: z.object({
        simple_panel: SimplePanelContentRuntype,
        pcb: z.string(),
        assembly_id: z.string().optional(),
    }),
});

export type CustomPanelPostDTO = z.infer<typeof CustomPanelPostRuntype>;
export const CustomPanelPostRuntype = z.object({
    type: z.literal('Panel'),
    data: z.object({
        pcb: z.string(),
        assembly_id: z.string().optional(),
        panel_details: PanelDetailsPostRuntype,
    }),
});

export type PanelPostDTO = z.infer<typeof PanelPostRuntype>;
export const PanelPostRuntype = z.union([CustomPanelPostRuntype, ExistingPanelPostRuntype, SimplePanelPostRuntype]);

export const PanelRenderRequestRuntype = z.object({
    panel_details: PanelPostRuntype,
});

// GET, differentiated because the ids are included in the response

export type ExistingPanelDTO = z.infer<typeof ExistingPanelRuntype>;
const ExistingPanelRuntype = z.object({
    type: z.literal('Existing'),
    data: ExistingPanelContentRuntype.extend({
        id: z.string(),
    }),
});

export type SimplePanelDTO = z.infer<typeof SimplePanelRuntype>;
const SimplePanelRuntype = z.object({
    type: z.literal('Simple'),
    data: z.object({
        simple_panel: SimplePanelContentRuntype.extend({ id: z.string() }),
        pcb: z.string(),
        assembly_id: z.string(),
    }),
});

const CustomPanelRuntype = z.object({
    type: z.literal('Panel'),
    data: z.object({
        pcb: z.string(),
        assembly_id: z.string(),
        panel_details: PanelDetailsPostRuntype.extend({
            id: z.string(),
        }),
    }),
});

export type PanelDTO = z.infer<typeof PanelRuntype>;
export const PanelRuntype = z.discriminatedUnion('type', [
    CustomPanelRuntype,
    ExistingPanelRuntype,
    SimplePanelRuntype,
]);

export const PanelDetailsRuntype = PanelDetailsPostRuntype.extend({ id: z.string() });

// PATCH
export type PanelizationFormModeDTO = z.infer<typeof PanelizationFormModeRuntype>;
const PanelizationFormModeRuntype = z.literal('Existing').or(z.literal('Details')).or(z.literal('Simple'));

export const PanelPatchRuntype = z
    .object({
        type: z.literal('Details'),
        data: PanelDetailsRuntype,
    })
    .or(ExistingPanelRuntype)
    .or(
        z.object({
            type: z.literal('Simple'),
            data: SimplePanelContentRuntype.extend({ id: z.string() }),
        }),
    );

export type CustomPartOfferPcbPanelSpecification = z.infer<typeof CustomPartOfferPcbPanelSpecificationRuntype>;
export const CustomPartOfferPcbPanelSpecificationRuntype = z.union([
    z.object({
        type: z.literal('PanelDetails'),
        data: PanelDetailsPostRuntype,
    }),
    z.object({
        type: z.literal('Existing'),
        data: ExistingPanelContentRuntype,
    }),
    z.object({
        type: z.literal('Simple'),
        data: SimplePanelContentRuntype,
    }),
]);

// Offer Panel Specification

export const PcbPanelSpecificationRuntype = z.object({
    type: z.literal('Pcb'),
    data: z.object({
        pcb: z.string(),
        panel: CustomPartOfferPcbPanelSpecificationRuntype,
    }),
});

export const PcbPanelUpdateSpecificationDTORuntype = z.object({
    type: z.literal('Pcb'),
    data: CustomPartOfferPcbPanelSpecificationRuntype,
});
